import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { Avatar } from '@/components/ui';
import { useAuthStore } from '@/stores/auth.store';
import { Brand } from './Brand';

export interface PageHeaderProps {
  title: string;
  /** Short status line under the title, e.g. "GW05 is open". */
  subtitle?: string | undefined;
}

/** Title block shown at the top of every authenticated screen. */
export function PageHeader({ title, subtitle }: PageHeaderProps) {
  const manager = useAuthStore((s) => s.manager);
  const { pathname } = useLocation();

  /**
   * The avatar is how you reach your profile, so it is pointless on the
   * profile screen — a control that navigates to where you already are.
   */
  const showAvatar = manager !== null && pathname !== ROUTES.profile;

  const avatarLink = (
    <Link
      to={ROUTES.profile}
      aria-label="Your profile"
      className="shrink-0 rounded-full outline-none transition hover:opacity-85 focus-visible:ring-2 focus-visible:ring-primary/60"
    >
      <Avatar name={manager?.name ?? ''} />
    </Link>
  );

  return (
    <>
      {/* Compact brand bar, mobile only — the sidebar covers this on desktop. */}
      <div className="mb-5 flex items-center justify-between gap-3 lg:hidden">
        <Brand />
        {showAvatar ? avatarLink : null}
      </div>

      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.1em] text-accent">
            Fantasy / 2026
          </p>
          <h1 className="text-2xl font-extrabold tracking-[-0.8px] sm:text-[28px]">{title}</h1>
          {subtitle ? (
            <span className="font-mono text-[11px] text-content-muted">{subtitle}</span>
          ) : null}
        </div>

        {showAvatar ? (
          <div className="hidden shrink-0 items-center gap-2.5 lg:flex">
            <span className="text-[13px] font-semibold">{manager?.name}</span>
            {avatarLink}
          </div>
        ) : null}
      </div>
    </>
  );
}
